import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import {
  Link,
  NavLink,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { Helmet } from "react-helmet";
import "./Techies.css";

type User = {
  email?: string | null;
  emailVerified?: boolean;
  id: number;
  handle: string;
  name: string;
  bio: string;
  skills: string[];
  github: string;
  role: string;
  created: number;
};
type Post = {
  id: number;
  kind: string;
  title: string;
  body: string;
  topic: string;
  created: number;
  updated: number;
  accepted_reply_id: number | null;
  author: User;
  replyCount: number;
};
type Reply = { id: number; body: string; created: number; author: User };
type Session = {
  user: User | null;
  csrf: string | null;
  recoveryCode?: string;
  verificationSent?: boolean;
};
const topics = [
  "Java",
  "JavaScript",
  "TypeScript",
  "Python",
  "React",
  "Spring Boot",
  "AI",
  "Cloud & DevOps",
  "System Design",
  "Career",
  "General",
];
const base = (
  process.env.REACT_APP_COMMUNITY_API_URL || "/community-api"
).replace(/\/+$/, "");
class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
  ) {
    super(message);
  }
}
async function api<T>(
  path: string,
  options: RequestInit = {},
  csrf: string | null = null,
): Promise<T> {
  const response = await fetch(base + path, {
    credentials: "same-origin",
    ...options,
    headers: {
      ...(options.body ? { "Content-Type": "application/json" } : {}),
      "X-Techies-Request": "1",
      ...(csrf ? { "X-CSRF-Token": csrf } : {}),
      ...options.headers,
    },
  });
  const data = await response.json().catch(() => ({
    error: "The community service is unavailable. Please try again.",
  }));
  if (!response.ok)
    throw new ApiError(data.error || "Something went wrong.", response.status);
  return data;
}
const CommunityContext = createContext<{
  user: User | null;
  loading: boolean;
  setSession: (session: Session) => void;
  request: <T>(path: string, method?: string, body?: unknown) => Promise<T>;
}>({
  user: null,
  loading: true,
  setSession: () => {},
  request: async () => {
    throw new Error("Missing community context");
  },
});
const useCommunity = () => useContext(CommunityContext);
const message = (error: unknown) =>
  error instanceof Error
    ? error.message
    : "Something went wrong. Please try again.";
const date = (value: number) =>
  new Date(value * 1000).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
function Author({ user }: { user: User }) {
  return (
    <Link className="tc-author" to={`/techies/u/${user.handle}`}>
      <span className="tc-avatar" aria-hidden="true">
        {user.name.slice(0, 1).toUpperCase()}
      </span>
      <span>
        {user.name}
        <small>
          @{user.handle}
          {user.role === "moderator" ? " · Moderator" : ""}
        </small>
      </span>
    </Link>
  );
}
function ErrorNotice({ error }: { error: string }) {
  return error ? (
    <p className="tc-error" role="alert">
      {error}
    </p>
  ) : null;
}
function SignInNotice({
  action = "join the conversation",
}: {
  action?: string;
}) {
  const location = useLocation();
  return (
    <div className="tc-empty">
      <h2>A little introduction first.</h2>
      <p>Sign in to {action}. Reading is open to everyone.</p>
      <Link
        className="ed-button"
        to={`/techies/login?next=${encodeURIComponent(location.pathname + location.search)}`}
      >
        Sign in ↗
      </Link>
      <Link
        className="tc-link"
        to={`/techies/join?next=${encodeURIComponent(location.pathname + location.search)}`}
      >
        Create your Techies account
      </Link>
    </div>
  );
}
function Body({ text }: { text: string }) {
  const chunks = text.split(/(```[\s\S]*?```)/g);
  return (
    <div className="tc-body">
      {chunks.map((chunk, index) =>
        chunk.startsWith("```") ? (
          <pre key={index}>
            <code>{chunk.slice(3, -3).replace(/^[a-zA-Z0-9+#.-]*\n/, "")}</code>
          </pre>
        ) : (
          <div key={index}>{chunk}</div>
        ),
      )}
    </div>
  );
}
function PostCard({ post }: { post: Post }) {
  return (
    <article className="tc-post-card">
      <div className="tc-post-stats">
        <strong>{post.replyCount}</strong>
        <span>{post.kind === "question" ? "answers" : "replies"}</span>
        {post.accepted_reply_id && <span className="tc-solved">✓ Solved</span>}
      </div>
      <div>
        <div className="tc-post-top">
          <Link
            className="tc-topic"
            to={`/techies?topic=${encodeURIComponent(post.topic)}`}
          >
            {post.topic}
          </Link>
          <span>{post.kind === "snippet" ? "Code snippet" : post.kind}</span>
        </div>
        <h2>
          <Link to={`/techies/posts/${post.id}`}>{post.title}</Link>
        </h2>
        <p className="tc-excerpt">
          {post.body.replace(/```/g, "").slice(0, 180)}
          {post.body.length > 180 ? "…" : ""}
        </p>
        <div className="tc-post-bottom">
          <Author user={post.author} />
          <time dateTime={new Date(post.created * 1000).toISOString()}>
            {date(post.created)}
          </time>
        </div>
      </div>
    </article>
  );
}

function Feed() {
  const [params, setParams] = useSearchParams();
  const key = params.toString();
  const [data, setData] = useState<{
    posts: Post[];
    total: number;
    page: number;
    hasMore: boolean;
  }>();
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [retry, setRetry] = useState(0);
  const [query, setQuery] = useState(params.get("q") || "");
  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError("");
    api<typeof data>("/posts?" + key, { signal: controller.signal })
      .then(setData)
      .catch((e) => {
        if (e.name !== "AbortError") setError(message(e));
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [key, retry]);
  function filter(name: string, value: string) {
    const next = new URLSearchParams(params);
    next.delete("page");
    value ? next.set(name, value) : next.delete(name);
    setParams(next);
  }
  return (
    <>
      <section className="tc-feed-heading">
        <div>
          <p className="ed-eyebrow">Nuhman Techies · The community</p>
          <h1>
            Good questions.
            <br />
            <em>Better together.</em>
          </h1>
        </div>
        <div>
          <p>
            Ask a question. Share something you’ve built.
            <br />
            Find your people, one conversation at a time.
          </p>
          <Link className="ed-button" to="/techies/new">
            Start a conversation ↗
          </Link>
        </div>
      </section>
      <div className="tc-feed-layout">
        <aside className="tc-sidebar">
          <h2>Find your corner</h2>
          <button
            className={!params.get("topic") ? "selected" : ""}
            onClick={() => filter("topic", "")}
          >
            All topics <span>↗</span>
          </button>
          {topics.map((topic) => (
            <button
              key={topic}
              className={params.get("topic") === topic ? "selected" : ""}
              onClick={() => filter("topic", topic)}
            >
              {topic}
            </button>
          ))}
          <div className="tc-guidelines">
            <span aria-hidden="true">✳</span>
            <h3>Make it a good place.</h3>
            <p>
              Be kind. Give context. Share what you’ve tried. Keep passwords and
              private data out of posts.
            </p>
          </div>
        </aside>
        <section className="tc-feed-main" aria-label="Community conversations">
          <form
            className="tc-search"
            onSubmit={(e) => {
              e.preventDefault();
              filter("q", query.trim());
            }}
          >
            <label className="tc-sr" htmlFor="community-search">
              Search conversations
            </label>
            <input
              id="community-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Find a question, idea, or snippet…"
              maxLength={120}
            />
            <button type="submit">Search ↗</button>
          </form>
          <div className="tc-feed-filters">
            {[
              ["", "Latest"],
              ["question", "Questions"],
              ["discussion", "Discussions"],
              ["snippet", "Snippets"],
            ].map(([value, label]) => (
              <button
                key={value}
                aria-pressed={(params.get("kind") || "") === value}
                onClick={() => filter("kind", value)}
              >
                {label}
              </button>
            ))}
            <label>
              <input
                type="checkbox"
                checked={params.get("unanswered") === "1"}
                onChange={(e) =>
                  filter("unanswered", e.target.checked ? "1" : "")
                }
              />
              Unresolved
            </label>
          </div>
          {params.get("topic") && (
            <p className="tc-filter-label">
              Exploring {params.get("topic")}{" "}
              <button onClick={() => filter("topic", "")}>Clear ×</button>
            </p>
          )}
          {loading ? (
            <p className="tc-loading" role="status">
              Opening the conversation…
            </p>
          ) : error ? (
            <div className="tc-empty">
              <ErrorNotice error={error} />
              <button
                className="tc-secondary"
                onClick={() => setRetry((n) => n + 1)}
              >
                Try again
              </button>
            </div>
          ) : data?.posts.length ? (
            <>
              <p className="tc-feed-count">
                {data.total}{" "}
                {data.total === 1 ? "conversation" : "conversations"} · Newest
                first
              </p>
              {data.posts.map((post) => (
                <PostCard key={post.id} post={post} />
              ))}
              <nav className="tc-pagination" aria-label="Feed pages">
                <button
                  disabled={data.page === 0}
                  onClick={() => {
                    const next = new URLSearchParams(params);
                    next.set("page", String(data.page - 1));
                    setParams(next);
                  }}
                >
                  ← Previous
                </button>
                <span>Page {data.page + 1}</span>
                <button
                  disabled={!data.hasMore}
                  onClick={() => {
                    const next = new URLSearchParams(params);
                    next.set("page", String(data.page + 1));
                    setParams(next);
                  }}
                >
                  Next →
                </button>
              </nav>
            </>
          ) : (
            <div className="tc-empty">
              <span className="tc-empty-mark" aria-hidden="true">
                {"{ }"}
              </span>
              <h2>
                {key
                  ? "No conversations found."
                  : "Every community starts with a hello."}
              </h2>
              <p>
                {key
                  ? "Try another topic or search, or start a conversation of your own."
                  : "Ask the question you’re working through. Share a useful snippet. Tell us what you’re building."}
              </p>
              <Link className="ed-button" to="/techies/new">
                Start the first conversation ↗
              </Link>
            </div>
          )}
        </section>
      </div>
    </>
  );
}

function Account({ mode }: { mode: "login" | "join" | "recover" }) {
  const { setSession } = useCommunity();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [recovery, setRecovery] = useState("");
  const [saved, setSaved] = useState(false);
  const [verificationSent, setVerificationSent] = useState<boolean>();
  const next = params.get("next");
  const destination =
    next && /^\/techies(?:\/|\?|$)/.test(next) ? next : "/techies";
  async function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setError("");
    const form = new FormData(event.currentTarget);
    try {
      const response = await api<Session>(
        "/auth/" + (mode === "join" ? "register" : mode),
        {
          method: "POST",
          body: JSON.stringify(Object.fromEntries(form.entries())),
        },
      );
      setSession(response);
      setVerificationSent(response.verificationSent);
      if (response.recoveryCode) setRecovery(response.recoveryCode);
      else navigate(destination);
    } catch (e) {
      setError(message(e));
    } finally {
      setBusy(false);
    }
  }
  function downloadRecovery() {
    const url = URL.createObjectURL(
      new Blob(
        [
          `Nuhman Techies recovery code\n\n${recovery}\n\nKeep this code private. Use it with your handle to reset your password at https://nuhman.com/#/techies/recover.\n`,
        ],
        { type: "text/plain" },
      ),
    );
    const a = document.createElement("a");
    a.href = url;
    a.download = "nuhman-techies-recovery.txt";
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
  return (
    <section className="tc-account">
      <p className="ed-eyebrow">A community for people who build</p>
      <h1>
        {recovery
          ? "Keep your way back."
          : mode === "join"
            ? "Find your people."
            : mode === "recover"
              ? "Let’s get you back."
              : "Welcome back."}
      </h1>
      {recovery ? (
        <>
          <p>
            This is your private recovery code. It is shown only now. Save it
            somewhere safe; you’ll need it to reset a forgotten password.
          </p>
          {mode === "join" && (
            <p role="status">
              {verificationSent
                ? "We’ve also sent a verification link to your email. Verify it before posting or replying."
                : "Your account was created, but the verification email could not be sent. Continue to request another email."}
            </p>
          )}
          <code className="tc-recovery-code">{recovery}</code>
          <button className="tc-secondary" onClick={downloadRecovery}>
            Download recovery code ↓
          </button>
          <label className="tc-check">
            <input
              type="checkbox"
              checked={saved}
              onChange={(e) => setSaved(e.target.checked)}
            />
            I’ve saved my recovery code securely.
          </label>
          <button
            className="ed-button"
            disabled={!saved}
            onClick={() =>
              navigate(mode === "join" ? "/techies/verify" : destination)
            }
          >
            Continue ↗
          </button>
        </>
      ) : (
        <>
          <p>
            {mode === "join"
              ? "Create a Techies account to ask, answer, and share. We’ll email you a verification link. Your email stays private. These accounts are separate from older Nuhman logins."
              : mode === "recover"
                ? "Use your handle and the private recovery code you saved when joining. A new code will replace it."
                : "Sign in with your Techies handle and password."}
          </p>
          <form onSubmit={submit}>
            <label>
              Handle
              <input
                name="handle"
                required
                minLength={3}
                maxLength={24}
                pattern="[a-zA-Z][a-zA-Z0-9_]{2,23}"
                autoComplete="username"
                placeholder="your_handle"
              />
            </label>
            {mode === "join" && (
              <label>
                Display name
                <input
                  name="name"
                  required
                  minLength={2}
                  maxLength={80}
                  autoComplete="name"
                  placeholder="How should people know you?"
                />
              </label>
            )}
            {mode === "join" && (
              <label>
                Email address
                <input
                  name="email"
                  type="email"
                  required
                  maxLength={254}
                  autoComplete="email"
                />
              </label>
            )}
            {mode === "recover" && (
              <label>
                Recovery code
                <input name="recoveryCode" required autoComplete="off" />
              </label>
            )}
            <label>
              {mode === "recover" ? "New password" : "Password"}
              <input
                name="password"
                type="password"
                required
                minLength={mode === "login" ? 1 : 12}
                maxLength={128}
                autoComplete={
                  mode === "login" ? "current-password" : "new-password"
                }
              />
            </label>
            {mode !== "login" && (
              <p className="tc-form-note">
                Use at least 12 characters. A password manager can help.
              </p>
            )}
            {mode === "join" && (
              <details className="tc-invite">
                <summary>Have a moderator invitation?</summary>
                <label>
                  Invitation code
                  <input name="invite" maxLength={100} autoComplete="off" />
                </label>
              </details>
            )}
            <ErrorNotice error={error} />
            <button className="ed-button" disabled={busy}>
              {busy
                ? "Please wait…"
                : mode === "join"
                  ? "Create account ↗"
                  : mode === "recover"
                    ? "Reset password ↗"
                    : "Sign in ↗"}
            </button>
          </form>
          <div className="tc-account-links">
            {mode === "login" ? (
              <>
                <Link
                  to={`/techies/join?next=${encodeURIComponent(destination)}`}
                >
                  Create an account
                </Link>
                <Link to="/techies/recover">Forgot your password?</Link>
              </>
            ) : (
              <Link
                to={`/techies/login?next=${encodeURIComponent(destination)}`}
              >
                Already a member? Sign in
              </Link>
            )}
          </div>
        </>
      )}
    </section>
  );
}

function VerifyEmail() {
  const { user, loading, request, setSession } = useCommunity();
  const [params, setParams] = useSearchParams();
  const token = params.get("token");
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [status, setStatus] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  async function verify(manualCode?: string) {
    setBusy(true);
    setError("");
    try {
      await request("/auth/verify-email", "POST", {
        token: manualCode || token,
      });
      setSession(await api<Session>("/session"));
      setDone(true);
      setParams({}, { replace: true });
    } catch (e) {
      setError(message(e));
    } finally {
      setBusy(false);
    }
  }
  async function resend(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setBusy(true);
    setError("");
    setStatus("");
    try {
      await request("/auth/email", "POST", { email: form.get("email") });
      setSession(await api<Session>("/session"));
      setStatus(
        "Verification email sent. Check your inbox and spam folder. Links expire in 24 hours. If the link does not work, paste the code from the email below.",
      );
    } catch (e) {
      setError(message(e));
    } finally {
      setBusy(false);
    }
  }
  if (loading) return <p role="status">Checking your account…</p>;
  return (
    <section className="tc-account">
      <p className="ed-eyebrow">One last step</p>
      <h1>
        {done || (!token && user?.emailVerified)
          ? "Email confirmed."
          : "Verify your email."}
      </h1>
      <ErrorNotice error={error} />
      {!done && !user?.emailVerified && (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            verify(code.trim());
          }}
        >
          <label>
            Verification code from your email
            <input
              value={code}
              onChange={(event) => setCode(event.target.value)}
              required
              minLength={20}
              maxLength={100}
              autoComplete="off"
              spellCheck={false}
            />
          </label>
          <button className="tc-secondary" disabled={busy}>
            {busy ? "Verifying…" : "Verify code ↗"}
          </button>
        </form>
      )}
      {done || (!token && user?.emailVerified) ? (
        <>
          <p>Your email is verified. You can now join the conversation.</p>
          <Link
            className="ed-button"
            to={user?.emailVerified ? "/techies" : "/techies/login"}
          >
            {user?.emailVerified
              ? "Back to Techies ↗"
              : "Sign in to continue ↗"}
          </Link>
        </>
      ) : token ? (
        <>
          <p>
            Confirm this email address for your Techies account. Verification
            links expire after 24 hours.
          </p>
          <button
            className="ed-button"
            disabled={busy}
            onClick={() => verify()}
          >
            {busy ? "Verifying…" : "Confirm email ↗"}
          </button>
          <Link className="tc-link" to="/techies/verify">
            Need a new verification link?
          </Link>
        </>
      ) : user ? (
        <>
          <p>
            Check your inbox for a message from Nuhman Techies. Confirm your
            email before posting, replying, or editing your profile. You can
            correct your address or request a new link below.
          </p>
          <form onSubmit={resend}>
            <label>
              Email address
              <input
                name="email"
                type="email"
                required
                maxLength={254}
                defaultValue={user.email || ""}
                autoComplete="email"
              />
            </label>
            <button className="ed-button" disabled={busy}>
              {busy ? "Sending…" : "Send verification email ↗"}
            </button>
          </form>
          <p role="status">{status}</p>
        </>
      ) : (
        <SignInNotice action="request a verification email" />
      )}
    </section>
  );
}

function Composer() {
  const { user, loading, request } = useCommunity();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [kind, setKind] = useState("question");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  async function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setError("");
    const form = new FormData(event.currentTarget);
    try {
      const data = await request<{ post: Post }>("/posts", "POST", {
        ...Object.fromEntries(form.entries()),
        kind,
      });
      navigate(`/techies/posts/${data.post.id}`);
    } catch (e) {
      setError(message(e));
    } finally {
      setBusy(false);
    }
  }
  if (loading) return <p role="status">Checking your session…</p>;
  if (!user) return <SignInNotice action="start a conversation" />;
  return (
    <section className="tc-compose">
      <Link className="tc-back" to="/techies">
        ← Back to the community
      </Link>
      <p className="ed-eyebrow">Share a little. Learn a lot.</p>
      <h1>Start something good.</h1>
      <p>
        Give people the context they need to join in. Don’t include passwords,
        tokens, or other private information.
      </p>
      <form onSubmit={submit}>
        <div className="tc-kind-picker" aria-label="Post type">
          {["question", "discussion", "snippet"].map((type) => (
            <button
              type="button"
              aria-pressed={kind === type}
              key={type}
              onClick={() => setKind(type)}
            >
              {type === "question"
                ? "? Question"
                : type === "discussion"
                  ? "↔ Discussion"
                  : "{ } Code snippet"}
            </button>
          ))}
        </div>
        <label>
          Title
          <input
            name="title"
            required
            minLength={10}
            maxLength={180}
            defaultValue={params.get("title") || ""}
            placeholder={
              kind === "question"
                ? "What are you trying to figure out?"
                : "What would you like to share?"
            }
          />
        </label>
        <label>
          Topic
          <select
            name="topic"
            defaultValue={
              topics.includes(params.get("topic") || "")
                ? params.get("topic")!
                : "General"
            }
          >
            {topics.map((topic) => (
              <option key={topic}>{topic}</option>
            ))}
          </select>
        </label>
        <label>
          {kind === "question" ? "Context and what you’ve tried" : "Your post"}
          <textarea
            name="body"
            rows={12}
            required
            minLength={20}
            maxLength={20000}
            placeholder={
              'Explain your idea or problem. Wrap code in triple backticks:\n```\nconst idea = "something useful";\n```'
            }
          />
        </label>
        <p className="tc-form-note">
          Your post is public. Code fences are supported; HTML is displayed as
          text.
        </p>
        <ErrorNotice error={error} />
        <button className="ed-button" disabled={busy}>
          {busy ? "Publishing…" : "Publish conversation ↗"}
        </button>
      </form>
    </section>
  );
}

function ReportButton({
  postId,
  replyId,
}: {
  postId: number;
  replyId?: number;
}) {
  const { user, request } = useCommunity();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  if (!user) return null;
  async function report(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      await request("/reports", "POST", { postId, replyId, reason });
      setSent(true);
      setOpen(false);
    } catch (e) {
      setError(message(e));
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="tc-report">
      {sent ? (
        <span role="status">Report sent to moderators.</span>
      ) : (
        <button className="tc-text-button" onClick={() => setOpen(!open)}>
          {open ? "Cancel report" : "Report"}
        </button>
      )}
      {open && (
        <form onSubmit={report}>
          <label>
            Why are you reporting this content?
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              required
              minLength={5}
              maxLength={1000}
              rows={3}
            />
          </label>
          <ErrorNotice error={error} />
          <button className="tc-secondary" disabled={busy}>
            {busy ? "Sending…" : "Send report"}
          </button>
        </form>
      )}
    </div>
  );
}
function RemoveButton({
  onRemove,
  label = "Remove",
  confirmation = "Remove this content?",
}: {
  onRemove: () => Promise<void>;
  label?: string;
  confirmation?: string;
}) {
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  return (
    <div className="tc-remove">
      {confirm ? (
        <>
          <span>{confirmation}</span>
          <button
            disabled={busy}
            className="tc-text-button"
            onClick={async () => {
              setBusy(true);
              try {
                await onRemove();
              } catch (e) {
                setError(message(e));
              } finally {
                setBusy(false);
              }
            }}
          >
            Confirm
          </button>
          <button className="tc-text-button" onClick={() => setConfirm(false)}>
            Cancel
          </button>
        </>
      ) : (
        <button className="tc-text-button" onClick={() => setConfirm(true)}>
          {label}
        </button>
      )}
      <ErrorNotice error={error} />
    </div>
  );
}

function Conversation() {
  const { id } = useParams();
  const { user, request } = useCommunity();
  const navigate = useNavigate();
  const [data, setData] = useState<{
    post: Post;
    replies: Reply[];
    page: number;
    hasMore: boolean;
  }>();
  const [page, setPage] = useState(0);
  const [error, setError] = useState("");
  const [actionError, setActionError] = useState("");
  const [loading, setLoading] = useState(true);
  const [revision, setRevision] = useState(0);
  const [busy, setBusy] = useState(false);
  const [reply, setReply] = useState("");
  useEffect(() => {
    setData(undefined);
    setActionError("");
    setPage(0);
    setReply("");
    window.scrollTo(0, 0);
  }, [id]);
  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError("");
    api<typeof data>(`/posts/${id}?page=${page}`, { signal: controller.signal })
      .then(setData)
      .catch((e) => {
        if (e.name !== "AbortError") setError(message(e));
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [id, page, revision]);
  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setActionError("");
    try {
      await request(`/posts/${id}/replies`, "POST", { body: reply });
      setReply("");
      setRevision((n) => n + 1);
    } catch (e) {
      setActionError(message(e));
    } finally {
      setBusy(false);
    }
  }
  async function accept(replyId: number | null) {
    setBusy(true);
    setActionError("");
    try {
      await request(`/posts/${id}/accept`, "POST", { replyId });
      setRevision((n) => n + 1);
    } catch (e) {
      setActionError(message(e));
    } finally {
      setBusy(false);
    }
  }
  if (loading && !data)
    return (
      <p role="status" className="tc-loading">
        Opening the conversation…
      </p>
    );
  if (error)
    return (
      <div className="tc-empty">
        <ErrorNotice error={error} />
        <button
          className="tc-secondary"
          onClick={() => setRevision((n) => n + 1)}
        >
          Try again
        </button>
        <Link className="tc-link" to="/techies">
          Back to the community
        </Link>
      </div>
    );
  if (!data) return null;
  const post = data.post;
  const own = user?.id === post.author.id;
  const canRemove = own || user?.role === "moderator";
  return (
    <section className="tc-conversation" aria-busy={loading}>
      <Link className="tc-back" to="/techies">
        ← Back to the community
      </Link>
      <div className="tc-post-top">
        <Link
          className="tc-topic"
          to={`/techies?topic=${encodeURIComponent(post.topic)}`}
        >
          {post.topic}
        </Link>
        <span>{post.kind}</span>
        {post.accepted_reply_id && <span className="tc-solved">✓ Solved</span>}
      </div>
      <h1>{post.title}</h1>
      <div className="tc-conversation-author">
        <Author user={post.author} />
        <time>{date(post.created)}</time>
      </div>
      <Body text={post.body} />
      <div className="tc-content-actions">
        <ReportButton postId={post.id} />
        {canRemove && (
          <RemoveButton
            onRemove={async () => {
              await request(`/posts/${id}`, "DELETE");
              navigate("/techies");
            }}
          />
        )}
      </div>
      <div className="tc-replies-heading">
        <h2>
          {post.replyCount}{" "}
          {post.kind === "question"
            ? post.replyCount === 1
              ? "answer"
              : "answers"
            : post.replyCount === 1
              ? "reply"
              : "replies"}
        </h2>
        <span>
          {post.kind === "question"
            ? "Useful answers move everyone forward."
            : "A conversation is better together."}
        </span>
      </div>
      {data.replies.length === 0 && (
        <p className="tc-muted">No replies yet. Bring your perspective.</p>
      )}
      {data.replies.map((item) => (
        <article
          className={`tc-reply ${post.accepted_reply_id === item.id ? "is-accepted" : ""}`}
          key={item.id}
        >
          {post.accepted_reply_id === item.id && (
            <p className="tc-accepted-label">
              ✓ Accepted by the question author
            </p>
          )}
          <div className="tc-conversation-author">
            <Author user={item.author} />
            <time>{date(item.created)}</time>
          </div>
          <Body text={item.body} />
          <div className="tc-content-actions">
            {own && post.kind === "question" && (
              <button
                className="tc-secondary"
                disabled={busy}
                onClick={() =>
                  accept(post.accepted_reply_id === item.id ? null : item.id)
                }
              >
                {post.accepted_reply_id === item.id
                  ? "Unmark accepted answer"
                  : "Accept answer ✓"}
              </button>
            )}
            <ReportButton postId={post.id} replyId={item.id} />
            {(user?.id === item.author.id || user?.role === "moderator") && (
              <RemoveButton
                onRemove={async () => {
                  await request(`/replies/${item.id}`, "DELETE");
                  setRevision((n) => n + 1);
                }}
              />
            )}
          </div>
        </article>
      ))}
      {(page > 0 || data.hasMore) && (
        <nav className="tc-pagination" aria-label="Reply pages">
          <button disabled={page === 0} onClick={() => setPage((n) => n - 1)}>
            ← Previous
          </button>
          <span>Page {page + 1}</span>
          <button
            disabled={!data.hasMore}
            onClick={() => setPage((n) => n + 1)}
          >
            Next →
          </button>
        </nav>
      )}
      <ErrorNotice error={actionError} />
      {user ? (
        <form className="tc-reply-form" onSubmit={submit}>
          <h2>
            Add your {post.kind === "question" ? "answer" : "perspective"}.
          </h2>
          <label>
            Your reply
            <textarea
              value={reply}
              onChange={(e) => setReply(e.target.value)}
              rows={6}
              required
              minLength={2}
              maxLength={10000}
              placeholder="Share something helpful. Code fences are welcome."
            />
          </label>
          <button className="ed-button" disabled={busy}>
            {busy ? "Posting…" : "Post reply ↗"}
          </button>
        </form>
      ) : (
        <SignInNotice action="add a reply" />
      )}
    </section>
  );
}

function Profile() {
  const { handle } = useParams();
  const { user, setSession, request } = useCommunity();
  const [data, setData] = useState<{ profile: User; posts: Post[] }>();
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [revision, setRevision] = useState(0);
  useEffect(() => {
    const controller = new AbortController();
    setData(undefined);
    setError("");
    setEditing(false);
    api<typeof data>(`/profiles/${encodeURIComponent(handle || "")}`, {
      signal: controller.signal,
    })
      .then(setData)
      .catch((e) => {
        if (e.name !== "AbortError") setError(message(e));
      });
    return () => controller.abort();
  }, [handle, revision]);
  async function save(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setError("");
    const form = new FormData(event.currentTarget);
    try {
      await request("/profile", "PATCH", {
        name: form.get("name"),
        bio: form.get("bio"),
        github: form.get("github"),
        skills: String(form.get("skills") || "")
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean),
      });
      const session = await api<Session>("/session");
      setSession(session);
      setRevision((n) => n + 1);
    } catch (e) {
      setError(message(e));
    } finally {
      setBusy(false);
    }
  }
  if (!data)
    return (
      <div className="tc-empty">
        {error ? (
          <ErrorNotice error={error} />
        ) : (
          <p role="status">Loading profile…</p>
        )}
      </div>
    );
  const profile = data.profile;
  return (
    <section className="tc-profile">
      <Link className="tc-back" to="/techies">
        ← Back to the community
      </Link>
      <div className="tc-profile-heading">
        <span className="tc-profile-avatar" aria-hidden="true">
          {profile.name[0].toUpperCase()}
        </span>
        <div>
          <p className="ed-eyebrow">
            {profile.role === "moderator"
              ? "Community moderator"
              : "One of the techies"}
          </p>
          <h1>{profile.name}</h1>
          <p>
            @{profile.handle} · Joined {date(profile.created)}
          </p>
        </div>
        {user?.id === profile.id && (
          <button className="tc-secondary" onClick={() => setEditing(!editing)}>
            {editing ? "Cancel editing" : "Edit profile"}
          </button>
        )}
      </div>
      {editing ? (
        <form className="tc-profile-edit" onSubmit={save}>
          <label>
            Display name
            <input
              name="name"
              defaultValue={profile.name}
              required
              minLength={2}
              maxLength={80}
            />
          </label>
          <label>
            Bio
            <textarea
              name="bio"
              defaultValue={profile.bio}
              maxLength={600}
              rows={4}
            />
          </label>
          <label>
            Skills, separated by commas
            <input
              name="skills"
              defaultValue={profile.skills.join(", ")}
              placeholder="TypeScript, React, Python"
            />
          </label>
          <label>
            GitHub profile URL
            <input
              name="github"
              type="url"
              defaultValue={profile.github}
              placeholder="https://github.com/yourname"
              maxLength={150}
            />
          </label>
          <ErrorNotice error={error} />
          <button className="ed-button" disabled={busy}>
            {busy ? "Saving…" : "Save profile ↗"}
          </button>
        </form>
      ) : (
        <>
          <p className="tc-bio">
            {profile.bio || "A bio is still in the making."}
          </p>
          <div className="tc-skills">
            {profile.skills.map((skill) => (
              <span key={skill}>{skill}</span>
            ))}
          </div>
          {profile.github && (
            <a
              className="tc-github"
              href={profile.github}
              target="_blank"
              rel="noopener noreferrer"
            >
              GitHub ↗
            </a>
          )}
        </>
      )}
      <h2 className="tc-profile-posts-heading">Recent conversations</h2>
      {data.posts.length ? (
        data.posts.map((post) => <PostCard key={post.id} post={post} />)
      ) : (
        <p className="tc-muted">No conversations yet.</p>
      )}
    </section>
  );
}

type Report = {
  id: number;
  post_id: number;
  reply_id: number | null;
  reason: string;
  title: string;
  post_body: string;
  reply_body: string | null;
  post_author: number;
  reply_author: number | null;
  post_hidden: number;
  reply_hidden: number | null;
  created: number;
};
function Moderation() {
  const { user, loading, request } = useCommunity();
  const [reports, setReports] = useState<Report[]>([]);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [revision, setRevision] = useState(0);
  useEffect(() => {
    if (user?.role === "moderator")
      api<{ reports: Report[] }>("/moderation/reports")
        .then((data) => setReports(data.reports))
        .catch((e) => setError(message(e)));
  }, [user, revision]);
  async function resolve(id: number) {
    setBusy(true);
    setError("");
    try {
      await request(`/moderation/reports/${id}/resolve`, "POST");
      setRevision((n) => n + 1);
    } catch (e) {
      setError(message(e));
    } finally {
      setBusy(false);
    }
  }
  if (loading) return <p role="status">Checking access…</p>;
  if (user?.role !== "moderator")
    return (
      <div className="tc-empty">
        <h1>Moderators only.</h1>
        <Link to="/techies">Back to the community</Link>
      </div>
    );
  return (
    <section className="tc-moderation">
      <p className="ed-eyebrow">Keep the community useful</p>
      <h1>Review the conversation.</h1>
      <ErrorNotice error={error} />
      {reports.length === 0 ? (
        <p className="tc-muted">No open reports.</p>
      ) : (
        reports.map((report) => (
          <article className="tc-report-card" key={report.id}>
            <p className="ed-eyebrow">
              {report.reply_id ? "Reported reply" : "Reported conversation"} ·{" "}
              {date(report.created)}
            </p>
            <h2>
              <Link to={`/techies/posts/${report.post_id}`}>
                {report.title}
              </Link>
            </h2>
            <p>
              <strong>Report reason:</strong> {report.reason}
            </p>
            <Body text={report.reply_body || report.post_body} />
            <div className="tc-content-actions">
              {(report.reply_id ? report.reply_hidden : report.post_hidden) ? (
                <span>Content removed</span>
              ) : (
                <RemoveButton
                  label="Remove reported content"
                  onRemove={async () => {
                    await request(
                      report.reply_id
                        ? `/replies/${report.reply_id}`
                        : `/posts/${report.post_id}`,
                      "DELETE",
                    );
                    setRevision((n) => n + 1);
                  }}
                />
              )}
              <RemoveButton
                confirmation="Suspend this account and hide all its posts and replies?"
                label="Suspend author"
                onRemove={async () => {
                  await request(
                    `/moderation/users/${report.reply_author || report.post_author}/suspend`,
                    "POST",
                  );
                  setRevision((n) => n + 1);
                }}
              />
              <button
                className="tc-secondary"
                disabled={busy}
                onClick={() => resolve(report.id)}
              >
                Mark resolved
              </button>
            </div>
          </article>
        ))
      )}
    </section>
  );
}

export default function Techies() {
  const [session, setSession] = useState<Session>({ user: null, csrf: null });
  const [loading, setLoading] = useState(true);
  const [sessionError, setSessionError] = useState("");
  const location = useLocation();
  useEffect(() => {
    api<Session>("/session")
      .then(setSession)
      .catch((e) => setSessionError(message(e)))
      .finally(() => setLoading(false));
  }, []);
  const request = useCallback(
    async <T,>(path: string, method = "POST", body?: unknown): Promise<T> => {
      try {
        return await api<T>(
          path,
          {
            method,
            body: body === undefined ? undefined : JSON.stringify(body),
          },
          session.csrf,
        );
      } catch (error) {
        if (error instanceof ApiError && error.status === 401)
          setSession({ user: null, csrf: null });
        throw error;
      }
    },
    [session.csrf],
  );
  async function logout() {
    try {
      await request("/auth/logout");
      setSession({ user: null, csrf: null });
    } catch (e) {
      setSessionError(message(e));
    }
  }
  return (
    <CommunityContext.Provider
      value={{ user: session.user, loading, setSession, request }}
    >
      <div className="ed-wrap ed-page tc-page">
        <Helmet>
          <title>Nuhman Techies — Questions, Code & Conversations</title>
          <meta
            name="description"
            content="A community for developers: ask technical questions, share code snippets, discuss ideas, and meet people building technology."
          />
        </Helmet>
        <div className="tc-bar">
          <Link to="/techies" className="tc-brand">
            techies<span> by nuhman</span>
          </Link>
          <nav aria-label="Community navigation">
            <NavLink to="/techies" end>
              Conversations
            </NavLink>
            {session.user ? (
              <>
                <Link to={`/techies/u/${session.user.handle}`}>My profile</Link>
                {session.user.role === "moderator" && (
                  <Link to="/techies/moderation">Moderation</Link>
                )}
                <button onClick={logout}>Sign out</button>
              </>
            ) : (
              !loading && (
                <>
                  <Link to="/techies/login">Sign in</Link>
                  <Link className="tc-join" to="/techies/join">
                    Join the community ↗
                  </Link>
                </>
              )
            )}
          </nav>
        </div>
        <ErrorNotice error={sessionError} />
        {session.user && !session.user.emailVerified && (
          <p className="tc-verification-notice">
            Verify your email to post and reply.{" "}
            <Link to="/techies/verify">
              Check your email or resend the link ↗
            </Link>
          </p>
        )}
        <Routes>
          <Route path="verify" element={<VerifyEmail />} />
          <Route index element={<Feed />} />
          <Route
            path="login"
            element={<Account key={"login" + location.search} mode="login" />}
          />
          <Route
            path="join"
            element={<Account key={"join" + location.search} mode="join" />}
          />
          <Route
            path="recover"
            element={<Account key="recover" mode="recover" />}
          />
          <Route path="new" element={<Composer />} />
          <Route path="posts/:id" element={<Conversation />} />
          <Route path="u/:handle" element={<Profile />} />
          <Route path="moderation" element={<Moderation />} />
          <Route
            path="*"
            element={
              <div className="tc-empty">
                <h1>That page isn’t here.</h1>
                <Link to="/techies">Back to the community</Link>
              </div>
            }
          />
        </Routes>
      </div>
    </CommunityContext.Provider>
  );
}
